import { useState } from 'react';
import { ApiError } from '@/services/api/client';
import { useUpdateSystemSettings } from '@/services/queries';
import { Button } from '@/shared/ui/button';
import { Card, CardContent, CardHeader } from '@/shared/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';
import { Input } from '@/shared/ui/input';
import { Label } from '@/shared/ui/label';
import { Switch } from '@/shared/ui/switch';
import type { SystemSettings } from '@/types';

function SettingRow({
  title,
  description,
  children,
}: {
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <div className="flex items-center justify-between gap-6 border-t border-border py-4 first:border-t-0 first:pt-0 last:pb-0">
      <div className="space-y-0.5">
        <p className="text-[13.5px] font-semibold">{title}</p>
        <p className="text-[12.5px] text-muted-foreground">{description}</p>
      </div>
      {children}
    </div>
  );
}

export function SecurityPolicyCard({ settings }: { settings: SystemSettings }) {
  const update = useUpdateSystemSettings();
  const [mfaDialogOpen, setMfaDialogOpen] = useState(false);
  const [code, setCode] = useState('');

  const mfaError = update.error instanceof ApiError ? update.error : null;

  const closeMfaDialog = () => {
    setMfaDialogOpen(false);
    setCode('');
  };

  return (
    <Card>
      <CardHeader>
        <p className="text-[15px] font-semibold">Security</p>
        <p className="text-[12.5px] text-muted-foreground">Applies to every NuDent staff account</p>
      </CardHeader>
      <CardContent className="pt-0">
        <SettingRow
          title="Require MFA"
          description="A second factor is checked on every admin login while this is on."
        >
          <Switch
            checked={settings.requireMfa}
            disabled={update.isPending}
            onCheckedChange={(checked) => {
              if (checked) {
                update.mutate({ requireMfa: true });
              } else {
                setMfaDialogOpen(true);
              }
            }}
          />
        </SettingRow>

        <SettingRow
          title="Allow licence reset without approval"
          description={
            settings.allowLicenseResetWithoutApproval
              ? 'When off, this cannot be turned back on from here.'
              : 'Restricted. This cannot be reopened from this screen.'
          }
        >
          <Switch
            checked={settings.allowLicenseResetWithoutApproval}
            disabled={update.isPending || !settings.allowLicenseResetWithoutApproval}
            onCheckedChange={() => {
              update.mutate({ allowLicenseResetWithoutApproval: false });
            }}
          />
        </SettingRow>
      </CardContent>

      <Dialog
        open={mfaDialogOpen}
        onOpenChange={(open) => {
          if (!open) closeMfaDialog();
        }}
      >
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Confirm it's you</DialogTitle>
            <DialogDescription>
              Turning MFA off removes the second factor for every admin login, including yours.
              Enter a current code from your authenticator app to confirm.
            </DialogDescription>
          </DialogHeader>

          {mfaError && (
            <p role="alert" className="text-sm text-destructive">
              {mfaError.message}
            </p>
          )}

          <div className="space-y-1.5">
            <Label htmlFor="require-mfa-code">Verification code</Label>
            <Input
              id="require-mfa-code"
              inputMode="numeric"
              autoComplete="one-time-code"
              maxLength={6}
              placeholder="000000"
              className="max-w-32 text-center font-mono tracking-widest"
              value={code}
              onChange={(event) => {
                setCode(event.target.value);
              }}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={closeMfaDialog}>
              Cancel
            </Button>
            <Button
              disabled={code.trim().length !== 6 || update.isPending}
              onClick={() => {
                update.mutate(
                  { requireMfa: false, code: code.trim() },
                  { onSuccess: closeMfaDialog },
                );
              }}
            >
              {update.isPending ? 'Turning off…' : 'Turn off'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
